import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { Button, Divider, Modal, Trans } from '@/components'
import { useTeamStore } from '@/stores/team'

import { AdminInstance } from './AdminInstanceTable'

export type BulkAction = 'snapshot' | 'changeOwner' | 'release'

interface BulkEditModalProps {
  open: boolean
  action: BulkAction
  instances: AdminInstance[]
  onClose: () => void
  onConfirm: () => void
}

export function BulkEditModal({ open, action, instances, onClose, onConfirm }: BulkEditModalProps) {
  const { t } = useTranslation()
  const teamStore = useTeamStore()
  const [processing, setProcessing] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const handleConfirm = async () => {
    setProcessing(true)
    setErrors([])

    if (action === 'snapshot') {
      await handleBulkSnapshot()
    } else if (action === 'release') {
      await handleBulkRelease()
    } else if (action === 'changeOwner') {
      // TODO: Implement when API is ready
      alert('Change Owner API is not ready yet')
    }

    setProcessing(false)
    if (errors.length === 0) {
      onConfirm()
    }
  }

  const handleBulkSnapshot = async () => {
    const results = await Promise.allSettled(
      instances.map(async (ins) => {
        try {
          await API.createSnapshot({
            id: ins.id,
            request_vol_id: [],
            snapshot_release_node: false,
            snapshot_required: true,
            snapshot_subject: `Bulk snapshot - ${ins.resID}`,
            status: 8,
          })
        } catch (error) {
          throw new Error(`${ins.resID}: ${error}`)
        }
      }),
    )

    const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[]
    if (failed.length > 0) {
      setErrors(failed.map((r) => r.reason.message))
    }
  }

  const handleBulkRelease = async () => {
    const teamResID = teamStore.teamTokenData?.teamResID
    if (!teamResID) {
      setErrors(['Team resource ID not found'])
      return
    }

    const results = await Promise.allSettled(
      instances.map(async (ins) => {
        try {
          await API.deleteTeamMemberInstance({
            res_id: teamResID,
            inst_res_id: ins.resID,
          })
        } catch (error) {
          throw new Error(`${ins.resID}: ${error}`)
        }
      }),
    )

    const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[]
    if (failed.length > 0) {
      setErrors(failed.map((r) => r.reason.message))
    }
  }

  const getTitle = () => {
    switch (action) {
      case 'snapshot':
        return t('team.admin.bulkEdit.snapshotTitle', 'Bulk Take Snapshot')
      case 'changeOwner':
        return t('team.admin.bulkEdit.changeOwnerTitle', 'Bulk Change Owner')
      case 'release':
        return t('team.admin.bulkEdit.releaseTitle', 'Bulk Release')
      default:
        return ''
    }
  }

  return (
    <Modal open={open} onClose={onClose} className="w-[800px]">
      <Modal.Header>{getTitle()}</Modal.Header>
      <Modal.Body>
        <div className="bg-panel-gray-100 rounded-lg p-4 max-h-40 overflow-auto">
          {instances.map((ins) => (
            <div key={ins.resID} className="h6 mb-1">
              {ins.displayTitle}
            </div>
          ))}
          {action === 'release' && (
            <div className="bg-panel-gray-100 rounded-lg">
              <Divider className="my-3" />
              <div>
                <div className="t-sb text-secondary font-medium">
                  <Trans i18nKey="instance.releaseModal.content">
                    The machine will be stopped and released, please save the environment if you
                    need it.
                  </Trans>
                </div>
                <div className="text-btn-secondary t-sb cursor-pointer">
                  <Trans i18nKey="instance.releaseModal.whySaveEnvironment">
                    Why save the environment?
                  </Trans>
                </div>
              </div>
            </div>
          )}
        </div>

        {errors.length > 0 && (
          <div className="mb-4">
            <div className="t-sb text-error mb-2">
              <Trans i18nKey="team.admin.bulkEdit.errors" values={{ count: errors.length }}>
                Failed: {{ count: errors.length }}
              </Trans>
            </div>
            <div className="bg-error/10 border border-error/40 rounded-lg p-3 max-h-32 overflow-auto">
              {errors.map((error, index) => (
                <div key={index} className="t-xs text-error mb-1">
                  {error}
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Divider />
      <Modal.Footer>
        <Button type="outline" onClick={onClose} disabled={processing}>
          <Trans i18nKey="common.cancel">Cancel</Trans>
        </Button>
        <Button type="danger" onClick={handleConfirm} disabled={processing}>
          {processing ? (
            <Trans i18nKey="common.processing">Processing...</Trans>
          ) : (
            <Trans i18nKey="instance.releaseModal.stopReleaseBtn">Stop & Release</Trans>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
